import React, { useRef, useMemo } from 'react'
import PropTypes from 'prop-types'

import {
  FixedContextMenu,
  Badge,
  toastShow,
  TOAST_VARIANT_SUCCESS,
  TOAST_VARIANT_WARNING
} from 'mailup-web-components'
import {
  MESSAGE_LIST_TYPE_NOT_ARCHIVED,
  MESSAGE_STR_TYPE_WELCOME
} from 'Constants'
import { useRecoilCallback, useSetRecoilState, useRecoilState } from 'recoil'
import {
  setPublicMessagesCallback,
  searchMessagesCallback,
  archiveMessagesCallback,
  unarchiveMessagesCallback
} from '../../recoil/callbacks/callbacks'
import { selectedResultsState } from '../../recoil/state/results'
import { modalDeleteState } from '../../recoil/state/page'
import { useIntl, FormattedMessage } from 'react-intl'
import {
  MESSAGE_LIST_TYPE_ALL,
  MESSAGE_LIST_TYPE_ARCHIVED,
  MESSAGE_LIST_TYPE_NOTIFICATION,
  MESSAGE_LIST_TYPE_PUBLISHED
} from 'Constants/index'

export const BulkActionsMenu = ({ resultsType, canDelete }) => {
  const intl = useIntl()
  const fetchResults = useRecoilCallback(searchMessagesCallback, [])
  const [selectedItems, setSelectedItems] = useRecoilState(selectedResultsState)

  const setModalDelete = useSetRecoilState(modalDeleteState)

  const setPublicMessages = useRecoilCallback(setPublicMessagesCallback, [])

  const archiveMessages = useRecoilCallback(archiveMessagesCallback, [])

  const unarchiveMessages = useRecoilCallback(unarchiveMessagesCallback, [])

  const { current: onPublishMessagesSuccess } = useRef(() => {
    toastShow(
      intl.formatMessage({ id: 'Load.EmailMessageList.PublishSuccess' }),
      TOAST_VARIANT_SUCCESS,
      'PublishSuccess',
      true
    )
    fetchResults()
  })

  const { current: onUnarchiveMessagesSuccess } = useRef(() => {
    toastShow(
      intl.formatMessage({ id: 'Load.MailupCommon.OperationSucces' }),
      TOAST_VARIANT_SUCCESS,
      'UnarchiveSuccess',
      true
    )
    fetchResults()
  })

  const { current: onUnPublishMessagesSuccess } = useRef(() => {
    toastShow(
      intl.formatMessage({ id: 'Load.EmailMessageList.OperationSucces' }),
      TOAST_VARIANT_SUCCESS,
      'onUnPublishMessagesSuccess',
      true
    )
    fetchResults()
  })

  const { current: onArchiveMessagesSuccess } = useRef(
    (toArchive, notToArchive) => {
      if (notToArchive.length === 1) {
        const warningMessage =
          notToArchive[0].MessageType === MESSAGE_STR_TYPE_WELCOME ? (
            <FormattedMessage
              id='Load.EmailMessageList.ArchiveMessageErrorWelcome'
              values={{
                strong: chunks => <strong>{chunks}</strong>,
                MGSNAME: notToArchive[0].Subject
              }}
            />
          ) : (
            <FormattedMessage
              id='Load.EmailMessageList.ArchiveMessageErrorConfirm'
              values={{
                strong: chunks => <strong>{chunks}</strong>,
                MGSNAME: notToArchive[0].Subject
              }}
            />
          )

        toastShow(
          warningMessage,
          TOAST_VARIANT_WARNING,
          'ArchiveMessageError',
          true
        )
      }
      if (notToArchive.length === 2) {
        const warningMessage = (
          <FormattedMessage
            id='Load.EmailMessageList.ArchiveMessageErrorMultiple'
            values={{
              strong: chunks => <strong>{chunks}</strong>,
              MESSAGECOUNT: notToArchive.length
            }}
          />
        )
        toastShow(
          warningMessage,
          TOAST_VARIANT_WARNING,
          'ArchiveMessageError',
          true
        )
      }

      if (toArchive.length) {
        toastShow(
          intl.formatMessage({ id: 'Load.EmailMessageList.ArchiveSuccess' }),
          TOAST_VARIANT_SUCCESS,
          'ArchiveSuccess',
          true
        )
        fetchResults()
      }
    }
  )

  const { current: handleClickDiscard } = useRef(() => {
    setSelectedItems([])
  })

  let actions = []
  //   export const MESSAGE_LIST_TYPE_ALL = 'all'
  // export const MESSAGE_LIST_TYPE_NOT_ARCHIVED = 'not-archived'
  // export const MESSAGE_LIST_TYPE_ARCHIVED = 'archived'
  // export const MESSAGE_LIST_TYPE_PUBLISHED = 'published'
  // export const MESSAGE_LIST_TYPE_NOTIFICATION = 'notification'

  const publish = {
    label: intl.formatMessage({
      id: 'Load.EmailMessageList.ActionFooterPublish'
    }),
    'data-mu-test': 'fixed-menu__publish',
    className: 'test-fixed-menu__publish',
    onClick: () =>
      setPublicMessages(selectedItems, true, onPublishMessagesSuccess)
  }

  const archive = {
    label: intl.formatMessage({
      id: 'Load.EmailMessageList.ActionFooterArchive'
    }),
    'data-mu-test': 'fixed-menu__archive',
    className: 'test-fixed-menu__archive',
    onClick: () => archiveMessages(selectedItems, onArchiveMessagesSuccess)
  }

  const unArchive = {
    label: intl.formatMessage({
      id: 'Load.EmailMessageList.ActionFooterNotArchive'
    }),
    'data-mu-test': 'fixed-menu__unarchive',
    className: 'test-fixed-menu__unarchive',
    onClick: () => unarchiveMessages(selectedItems, onUnarchiveMessagesSuccess)
  }

  const unPublish = {
    label: intl.formatMessage({
      id: 'Load.EmailMessageList.ActionFooterUnpublish'
    }),
    'data-mu-test': 'fixed-menu__unpublish',
    className: 'test-fixed-menu__unpublish',
    onClick: () =>
      setPublicMessages(selectedItems, false, onUnPublishMessagesSuccess)
  }
  const deleteM = {
    label: intl.formatMessage({
      id: 'Load.MailupCommon.Delete'
    }),
    variant: 'danger',
    onClick: () => {
      setModalDelete({ show: true, items: [...selectedItems] })
    },
    'data-mu-test': 'fixed-menu__delete',
    className: 'test-fixed-menu__delete'
  }

  if (resultsType === MESSAGE_LIST_TYPE_ALL) {
    actions = [publish, archive, ...(canDelete ? [deleteM] : [])]
  } else if (resultsType === MESSAGE_LIST_TYPE_NOT_ARCHIVED) {
    actions = [publish, archive, ...(canDelete ? [deleteM] : [])]
  } else if (resultsType === MESSAGE_LIST_TYPE_ARCHIVED) {
    actions = [publish, unArchive, ...(canDelete ? [deleteM] : [])]
  } else if (resultsType === MESSAGE_LIST_TYPE_PUBLISHED) {
    actions = [archive, unPublish, ...(canDelete ? [deleteM] : [])]
  } else if (resultsType === MESSAGE_LIST_TYPE_NOTIFICATION) {
    actions = [publish, archive, ...(canDelete ? [deleteM] : [])]
  }

  return (
    <FixedContextMenu
      visible={!!selectedItems.length}
      label={
        <>
          <Badge type='success' kind='primary' text={selectedItems.length} />
          {intl.formatMessage({
            id: 'Load.EmailMessageList.ActionSelectedLabel'
          })}
        </>
      }
      actionDiscard={{
        label: intl.formatMessage({ id: 'Load.Common.CancelSelection' }),
        onClick: handleClickDiscard,
        'data-mu-test': 'fixed-menu__cancel'
      }}
      actions={actions}
    />
  )
}

BulkActionsMenu.propTypes = {
  resultsType: PropTypes.string,
  canDelete: PropTypes.bool
}
